import { CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { MedianPriceLink } from 'components/MedianPriceLink'
import { type InputType, FieldOrigin, IVisibleProp, MultiPropertyFields, sandBoxNotEditableFields } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { combinedPropertyMonthlyExpense } from 'services/pdfs/utils'
import { onSplitText } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import type { ILoan, ILoanDetail, ILoanProcess, IMultipleProperty, IPropertyChangedJson } from './interfaces'

interface IProps {
  targetIndex: number
  loan: ILoan
  propertyData: IMultipleProperty
  loanDetail: ILoanDetail
  loanProcessData: ILoanProcess
  visibleFields: string[]
  loanFields: Record<string, InputType>
  brokerCanNotEditDetail: boolean
  sandBoxMode: boolean
  canEditLoanDetail: boolean
  exceptionApproved: boolean
  canPriceAdjust: boolean
  isBorrower?: boolean
  anytimeCanEditLoanDetail: boolean | undefined
  changedPropertyDetailJson: IPropertyChangedJson[]
  priceAdjustmentObject: any
  onChangeInputFields: (key: string, e: any) => void
  onBlur: (key: string) => void
  onRemove: () => void
}

export const MultiPropertyInputs = (props: IProps) => {
  const {
    targetIndex,
    loan,
    loanFields,
    propertyData,
    loanDetail,
    loanProcessData,
    visibleFields,
    brokerCanNotEditDetail,
    changedPropertyDetailJson,
    priceAdjustmentObject,
    sandBoxMode,
    canEditLoanDetail,
    exceptionApproved,
    anytimeCanEditLoanDetail,
    canPriceAdjust,
    isBorrower = false,
    onChangeInputFields,
    onBlur,
    onRemove,
  } = props

  const [tbdUpdateTry, setTBDUpdateTry] = useState(0)

  const { minTotalConstructionFinanced } = loanDetail.loanGlobalSettings

  useEffect(() => {
    if (tbdUpdateTry > 0) onBlur('subjectPropertyAddressTBD')
  }, [tbdUpdateTry])

  const { fieldProperties } = useSelector((state: any) => ({
    fieldProperties: ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[FieldOrigin.LoanStructure] ||
      {}) as Record<string, IVisibleProp>,
  }))

  const hideCancelButton = useMemo(() => {
    if (Number(loanDetail.rateData?.price) > 0 && isBorrower) return true
    if (sandBoxMode) return false
    else {
      if (exceptionApproved && !anytimeCanEditLoanDetail) return true
      if (canEditLoanDetail) return false
      else return true
    }
  }, [loanDetail, isBorrower, sandBoxMode, exceptionApproved, anytimeCanEditLoanDetail, canEditLoanDetail])

  const disableLoanDetailField = (key: string) => {
    if (Number(loanDetail.rateData?.price) > 0 && isBorrower) return true
    if (key === 'estimatedCreditScore' && loanDetail.submitToLoanSetup === 0) return false

    if (sandBoxMode) {
      if (sandBoxNotEditableFields.indexOf(key) !== -1) return true
      return false
    } else {
      if (exceptionApproved && !anytimeCanEditLoanDetail) return true
      if (canEditLoanDetail) return false
      return true
    }
  }

  const onChangeTBD = async () => {
    await onChangeInputFields('subjectPropertyAddressTBD', !propertyData?.subjectPropertyAddressTBD)
    setTimeout(() => {
      setTBDUpdateTry(tbdUpdateTry + 1)
    }, 300)
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 rounded border border-solid border-gray-200 shadow1 p-2 justify-around">
      <div className="col-span-2 flex justify-between -mb-2">
        <div className="text-sm font-variation-settings-600">Property {targetIndex + 2}</div>
        {!hideCancelButton && (
          <div
            className="w-fit p-1 transition-all duration-200 hover:cursor-pointer hover-shadow1 rounded"
            onClick={onRemove}
          >
            <TrashIcon className="w-4 h-4 hover:cursor-pointer text-red-700" />
          </div>
        )}
      </div>
      {MultiPropertyFields.map((key, index) => {
        if (!visibleFields.includes(key)) return <></>
        let rlt = []
        let cn = loanFields[key]?.length === 1 ? 'md:col-span-2' : 'md:col-span-1'
        let tooltip = fieldProperties[key] && fieldProperties[key].tooltip ? fieldProperties[key].tooltip : ''
        let additionalElements = null
        let disabled = false
        if (!loanFields[key]) {
          console.log('Unknown Field', key)
          return null
        }
        let readOnly = loanFields[key]?.readOnly
        if (!priceAdjustmentObject[key] && key === 'constructionReserve' && minTotalConstructionFinanced === 100)
          readOnly = true
        if (brokerCanNotEditDetail || exceptionApproved) {
          const changedData = changedPropertyDetailJson.find((item) => item.key === key)
          if (!!changedData) {
            cn += ' ring-2 ring-red-400 rounded-t'
            const changeString = `${changedData.origin} -> ${changedData.changed}`
            if (tooltip) tooltip = `${tooltip}<br/>${changeString}`
            else tooltip = changeString
          }
        }
        if (key === 'subjectPropertyAddress') {
          additionalElements = (
            <div className="flex items-center gap-2">
              <MedianPriceLink address={propertyData?.subjectPropertyAddress} />
              <div className="flex items-center gap-0.5 hover:cursor-pointer" onClick={onChangeTBD}>
                {!!propertyData?.subjectPropertyAddressTBD ? (
                  <CheckIcon className="w-4 h-4 text-shade-blue" />
                ) : (
                  <XMarkIcon className="w-4 h-4 text-red-700" />
                )}
                <span className="text-[12px]">TBD</span>
              </div>
            </div>
          )
          loanFields[key].required = !propertyData?.subjectPropertyAddressTBD
        }
        if (key !== 'estimatedDscr')
          rlt.push(
            <div key={index} className={cn}>
              <RenderInput
                input={{
                  ...loanFields[key],
                  error:
                    priceAdjustmentObject[key] !== undefined && (propertyData as any)[key] !== ''
                      ? ''
                      : loanFields[key].error,
                  value: (propertyData as any)[key] ?? '',
                  history: false,
                  disabled: (disableLoanDetailField(key) ? !canPriceAdjust : false) || disabled,
                  tooltip: tooltip ? onSplitText(tooltip, 70) : tooltip,
                  additionalElements: isBorrower ? null : additionalElements,
                  readOnly,
                }}
                Key={key}
                onChange={onChangeInputFields}
                onBlur={onBlur}
              />
            </div>,
          )

        if (key === 'estimatedDscr') {
          const pitis = combinedPropertyMonthlyExpense(loan, propertyData, loanProcessData)
          if (pitis['dscr'] !== null) {
            let tooltip = `${propertyData.proposedMonthlyRent} = (Rent) <br/>/<br/> ${pitis['sum']} = (Mortgage Payment:${pitis['mortgagePayment']} + Insurance + Taxes + HoaDues + <br/> Second Lien Payment)`
            let classNames = 'md:col-span-1'
            if (brokerCanNotEditDetail || exceptionApproved) {
              const dscrChanged = changedPropertyDetailJson.find((item) => item.key === 'dscr')
              if (!!dscrChanged) {
                classNames += ' ring-2 ring-red-400 rounded-t'
                const changeString = `${dscrChanged.origin} -> ${dscrChanged.changed}`
                if (tooltip) tooltip = `${tooltip}<br/><br/>${changeString}`
                else tooltip = changeString
              }
            }
            rlt.push(
              <div key={`calculatedDSCR-${index}`} className={classNames}>
                <RenderInput
                  input={{
                    title: 'Calculated DSCR',
                    inputType: 'text',
                    prefix: '%',
                    tooltip,
                    value: pitis['dscr'],
                    readOnly: true,
                  }}
                  Key={'estimatedDscr'}
                  onChange={() => {}}
                />
              </div>,
            )
          }
        }
        return rlt
      })}
    </div>
  )
}
